import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import useToastContext from 'hooks/useToastContext'
import { validationRules } from 'utils/constants'
import { useTranslation } from 'react-i18next'
import { apiPut } from 'utils/apiFetch'
import apiPath from 'utils/apiPath'
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai'
import { preventMaxInput } from 'utils/validations'
import OInputField from 'components/reusable/OInputField'
import { isEmpty } from 'lodash'
import helpers from 'utils/helpers'

const CompanyPassword = ({ setPasswordShowModal, item }) => {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    reset,
    watch,
    trigger,
    formState: { errors, isDirty }
  } = useForm({ mode: 'onChange', shouldFocusError: true, defaultValues: {} })
  const [changePasswordLoading, setChangePasswordLoading] = useState(false)
  const notification = useToastContext()
  const [icon2, setIcon2] = useState(true)
  const [icon3, setIcon3] = useState(true)

  const handleSubmitForm = async data => {
    try {
      if (!isDirty) {
        notification.info('Please enter something.')
        return
      }
      setChangePasswordLoading(true)

      const res = await apiPut(
        apiPath.CompanyChangePassword + '/' + item?._id,
        {
          newPassword: data.newPassword
        }
      )
      if (res.data.success) {
        reset()
        notification.success(res?.data?.message)
        setPasswordShowModal(false)
      } else {
        notification.error(res?.data?.message)
      }
    } catch (err) {
      console.log('err:', err)
    } finally {
      setChangePasswordLoading(false)
    }
  }

  function changeIcon2 () {
    setIcon2(!icon2)
  }
  function changeIcon3 () {
    setIcon3(!icon3)
  }

  const newPassword = watch('newPassword')
  useEffect(() => {
    if (!isEmpty(newPassword)) {
      trigger('confirm_password')
    }
  }, [newPassword, trigger])

  const renderFooter = () => (
    <div className='flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b'>
      <button
        className='text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6  ease-linear transition-all duration-150'
        type='button'
        onClick={() => setPasswordShowModal(false)}
      >
        {t('O_BACK')}
      </button>

      {changePasswordLoading ? (
        <div className='spinner-container bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 ease-linear transition-all duration-150'>
          <div className='loading-spinner'></div>
        </div>
      ) : (
        <button
          className='bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1  ease-linear transition-all duration-150'
          type='submit'
          onClick={handleSubmit(handleSubmitForm)}
        >
          {t('O_SUBMIT')}
        </button>
      )}
    </div>
  )

  return (
    <>
      <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
        <div className='relative w-auto my-6 mx-auto max-w-3xl'>
          <form className='sm:py-4 sm:px-2 py-8 px-7'>
            <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none min-w-[762px]'>
              <div className='flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t'>
                <h3 className='text-xl font-semibold'>
                  {t('CHANGE_PASSWORD')}
                </h3>
                <button
                  className=' ml-auto flex items-center justify-center  text-black border-2 rounded-full  h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none'
                  onClick={() => setPasswordShowModal(false)}
                >
                  <span className=' text-[#B8BBBF]  text-4xl ' title='Close'>
                    ×
                  </span>
                </button>
              </div>
              <div className='grid md:grid-cols-2 sm:grid-cols-2'>
                <div className='py-4 px-4 '>
                  <div className='relative'>
                    <OInputField
                      name='newPassword'
                      id='newPassword'
                      errors={errors}
                      wrapperClassName='relative z-0 w-full group'
                      type={helpers?.ternaryCondition(
                        icon2,
                        'password',
                        'text'
                      )}
                      inputLabel={
                        <>
                          {t('CHANGE_PASSWORD_NEW_PASSWORD')}
                          <span className='text-red-500'>*</span>
                        </>
                      }
                      onInput={e => preventMaxInput(e, 15)}
                      register={register('newPassword', {
                        required: 'Please enter new password.',
                        pattern: {
                          value: validationRules.password,
                          message: validationRules.newPasswordMessage
                        }
                      })}
                    />
                    {icon2 ? (
                      <span
                        onClick={() => changeIcon2()}
                        className='top-[18px] right-[20px] password_view absolute '
                      >
                        <AiFillEyeInvisible />
                      </span>
                    ) : (
                      <span
                        className='password_view right-[20px] absolute top-[18px] '
                        onClick={() => changeIcon2()}
                      >
                        <AiFillEye />
                      </span>
                    )}
                  </div>
                </div>
                <div className='px-4 py-4 '>
                  <div className='relative'>
                    <OInputField
                      wrapperClassName='relative z-0 w-full group'
                      type={helpers?.ternaryCondition(
                        icon3,
                        'password',
                        'text'
                      )}
                      name='confirm_password'
                      id='confirm_password'
                      errors={errors}
                      inputLabel={
                        <>
                          {t('CHANGE_PASSWORD_CONFIRM_PASSWORD')}
                          <span className='text-red-500'>*</span>
                        </>
                      }
                      onInput={e => preventMaxInput(e, 15)}
                      register={register('confirm_password', {
                        required: 'Please enter confirm password.',
                        pattern: {
                          value: validationRules.password,
                          message: validationRules.confirmPasswordMessage
                        },
                        validate: val => {
                          if (watch('newPassword') !== val) {
                            return 'New password and confirm password does not match.'
                          }
                        }
                      })}
                    />
                    {icon3 ? (
                      <span
                        className='password_view absolute top-[18px] right-[20px]'
                        onClick={() => changeIcon3()}
                      >
                        <AiFillEyeInvisible />
                      </span>
                    ) : (
                      <span
                        className='password_view absolute top-[18px] right-[20px]'
                        onClick={() => changeIcon3()}
                      >
                        <AiFillEye />
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {renderFooter()}
            </div>
          </form>
        </div>
      </div>
      <div className='opacity-25 fixed inset-0 z-40 bg-black' />
    </>
  )
}

export default CompanyPassword
